"use client";

import React from "react";
import { Phone, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Editable copy constants
const OFFER_EYEBROW = "NEW TIRE OFFERS";
const OFFER_HEADING = "Buying Four New Tires? Ask About Current Offers.";
const OFFER_DESCRIPTION = "Offers change with tire size and current availability. Call us with your tire size to check today's options.";
const OFFER_CTA_TEXT = "Check Current Offers";
const OFFER_PHONE_NUMBER = "+14243138474";
const OFFER_DISCLAIMER = "Offer details will be confirmed before installation.";
export default function TireOffer() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="tireoffer" data-nav="light" className="bg-background text-foreground py-8 sm:py-11 md:py-14 transition-colors overflow-hidden">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.4,
        ease: "easeOut"
      }} className="relative overflow-hidden rounded-2xl border-2 border-t-4 border-primary/20 border-t-[#FF5A00] bg-card text-card-foreground p-5 sm:p-6 md:p-8 shadow-lg">
          {/* Subtle decorative tire tread / radial pattern */}
          <div className="pointer-events-none absolute -right-16 -top-16 size-72 sm:size-96 opacity-10 text-primary" aria-hidden="true">
            <svg viewBox="0 0 200 200" className="size-full fill-none stroke-current stroke-[2.5]">
              {/* Concentric tread rings */}
              <circle cx="100" cy="100" r="90" strokeDasharray="4 8" />
              <circle cx="100" cy="100" r="75" strokeDasharray="6 6" />
              <circle cx="100" cy="100" r="60" strokeDasharray="3 7" />
              <circle cx="100" cy="100" r="45" strokeDasharray="5 5" />
              <circle cx="100" cy="100" r="30" strokeDasharray="2 4" />
              {/* Radial tread notches */}
              <path d="M100 10 L100 30 M100 170 L100 190 M10 100 L30 100 M170 100 L190 100" />
              <path d="M36 36 L50 50 M150 150 L164 164 M36 164 L50 150 M150 50 L164 36" />
            </svg>
          </div>

          <div className="pointer-events-none absolute -bottom-20 -left-20 size-64 opacity-5 text-primary" aria-hidden="true">
            <svg viewBox="0 0 200 200" className="size-full fill-none stroke-current stroke-2">
              <circle cx="100" cy="100" r="80" strokeDasharray="8 6" />
              <circle cx="100" cy="100" r="60" strokeDasharray="4 4" />
              <circle cx="100" cy="100" r="40" strokeDasharray="2 6" />
            </svg>
          </div>

          <div className="relative z-10 flex flex-col items-start max-w-3xl w-full min-w-0">
            {/* Eyebrow badge */}
            <div className="mb-4 max-w-full">
              <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary font-semibold tracking-wide uppercase px-3 py-1 text-xs max-w-full">
                <Sparkles className="size-3.5 mr-1 shrink-0" />
                <span className="truncate">{OFFER_EYEBROW}</span>
              </Badge>
            </div>

            {/* Headline */}
            <h2 className="font-default text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground leading-tight break-words max-w-full">
              {OFFER_HEADING}
            </h2>

            {/* Paragraph */}
            <p className="mt-4 text-base sm:text-lg text-foreground/80 leading-relaxed max-w-2xl break-words">
              {OFFER_DESCRIPTION}
            </p>

            {/* CTA action wrapper */}
            <div className="mt-8 flex flex-col items-start gap-3 w-full sm:w-auto max-w-full min-w-0">
              <Button asChild size="lg" className="w-full sm:w-auto max-w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md transition-[color,background-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 text-base font-semibold px-6 py-5 rounded-md justify-center">
                <a href={`tel:${OFFER_PHONE_NUMBER}`} className="inline-flex items-center justify-center gap-2 max-w-full">
                  <Phone className="size-4 shrink-0" />
                  <span className="break-words">{OFFER_CTA_TEXT}</span>
                </a>
              </Button>

              {/* Supporting small note */}
              <p className="text-xs sm:text-sm text-foreground/60 leading-normal break-words max-w-full">
                {OFFER_DISCLAIMER}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}